import {
  useBreakpoint,
  useTaskById,
  useQueryParams,
  useLeftPanelState,
  useLeftPanelActions,
  useRightSidePanelActions,
  useRightSidePanelState,
  useAttachedImageTooltipState,
  useAttachedImageTooltipActions,
  useCompareMode,
  useComparingConversationsParams,
  useColorTheme,
  useUniqueId,
  useSkillCommand,
  useActiveModel,
  useChatHistory,
  useGetUser,
} from 'src/hooks';
import { ChatCircleText, NotePencil } from '@phosphor-icons/react';
import { AppRoutes, GTMEvent } from 'src/types';
import { SVG_SIZE_M, SVG_SIZE_XXL } from 'src/constants';
import { sendGTMEvent } from 'src/utils';
import { Icon } from 'src/components/Icon';
import { Button } from 'src/v2/commonComponents/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import lightIcon from 'src/images/icons/new_chat_icon_light.svg';
import darkIcon from 'src/images/icons/new_chat_icon_dark.svg';
import styles from './AddConversationButton.module.scss';
import { env } from 'src/env';

interface AddConversationButtonProps {
  appearance:
    | 'ninja-icon'
    | 'pencil-icon'
    | 'transparent-button'
    | 'gradient-plus-button';
}

const NEW_CHAT_HOVER_TOOLTIP_ID = 'new-chat-tooltip';

export const AddConversationButton = ({
  appearance,
}: AddConversationButtonProps) => {
  const { isNinjaRetired } = useChatHistory();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { searchParams, removeSearchParam } = useQueryParams();

  const { toggleLeftPanelExpanded } = useLeftPanelActions();
  const { isLeftPanelExpanded } = useLeftPanelState();

  const { isMobileOrTablet } = useBreakpoint();
  const { isDarkTheme } = useColorTheme();

  const { removeCurrentTaskId } = useTaskById();
  const { resetCompareMode } = useCompareMode();
  const { isCompareMode } = useComparingConversationsParams();
  const { isImageEditSectionExpanded } = useRightSidePanelState();
  const { isAttachedImageEditingMode } = useAttachedImageTooltipState();
  const { toggleRightSidePanelExpanded } = useRightSidePanelActions();
  const { uniqueId } = useUniqueId();
  const { toggleActiveSkillCommand } = useSkillCommand();
  const { changeInputBoxActiveModel } = useActiveModel();

  const {
    onChangeIsAttachedImageEditingMode,
    onToggleImageEditTooltipVisible,
  } = useAttachedImageTooltipActions();

  const { isOpenTier } = useGetUser();

  const handleCreateNewChat = async () => {
    if (isNinjaRetired) {
      const url = `${env.REACT_APP_SUPER_NINJA_URL}${!isOpenTier ? 'dashboard' : ''}`;
      window.open(url);
      return;
    }

    toggleActiveSkillCommand(null);
    changeInputBoxActiveModel();

    if (pathname !== AppRoutes.HOME) {
      navigate(AppRoutes.HOME);
      return;
    }

    if (isCompareMode) {
      navigate(AppRoutes.HOME);
      resetCompareMode();
      return;
    }

    if (!!searchParams.conv) {
      removeCurrentTaskId();
      removeSearchParam('conv');
      sendGTMEvent(GTMEvent.NEW_CHAT_INITIATION);

      if (isImageEditSectionExpanded) {
        toggleRightSidePanelExpanded(false);
      }
    }

    if (isAttachedImageEditingMode) {
      toggleRightSidePanelExpanded(false);
      onChangeIsAttachedImageEditingMode(false);
      onToggleImageEditTooltipVisible(true);
    }

    if (
      (isMobileOrTablet || isImageEditSectionExpanded) &&
      isLeftPanelExpanded
    ) {
      toggleLeftPanelExpanded();
    }

    const event = new CustomEvent('refresh-pillars', {
      detail: { message: 'Refresh random pillars' },
    });
    dispatchEvent(event);
  };

  if (appearance === 'ninja-icon') {
    return (
      <Button
        color="transparent"
        shape="round"
        className={styles.ninjaIcon}
        onClick={handleCreateNewChat}
      >
        <Icon
          type={isNinjaRetired ? 'superNinjaMainIcon' : 'ninjaMainIcon'}
          size={SVG_SIZE_XXL}
        />
      </Button>
    );
  }

  if (appearance === 'pencil-icon') {
    return (
      <Button color="transparent" shape="round" onClick={handleCreateNewChat}>
        <NotePencil size={SVG_SIZE_M} />
      </Button>
    );
  }

  if (appearance === 'transparent-button') {
    return (
      <Button
        fullWidthWithLeftAlign
        color="transparent"
        onClick={handleCreateNewChat}
      >
        <ChatCircleText size={SVG_SIZE_M} />
        New Chat
      </Button>
    );
  }

  if (appearance === 'gradient-plus-button') {
    return (
      <Button
        color="transparent"
        shape="round"
        onClick={handleCreateNewChat}
        className={styles.gradientIconButton}
        tooltipId={`${NEW_CHAT_HOVER_TOOLTIP_ID}-${uniqueId}`}
        tooltipContent="New chat"
        tooltipPlace="bottom-end"
      >
        <img
          referrerPolicy="no-referrer"
          src={isDarkTheme ? darkIcon : lightIcon}
          alt="Ninjatech AI"
          width={44}
          height={44}
        />
      </Button>
    );
  }

  return null;
};
